import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";

const EMPTY = { name: "", tagline: "", eta: "" };
const input = "w-full bg-transparent border-b border-border py-2 font-mono text-sm focus:border-signal outline-none";

function DropRow({ drop: d, busy, onSave, onGolive }) {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState(EMPTY);

  const startEdit = () => {
    setForm({ name: d.name, tagline: d.tagline || "", eta: d.eta || "" });
    setEditing(true);
  };
  const save = async () => {
    const ok = await onSave(d.id, form);
    if (ok) setEditing(false);
  };

  if (editing) {
    return (
      <div className="border border-border p-5" data-testid={`drop-row-${d.id}`}>
        <div className="space-y-3">
          <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
            className={input} placeholder="Drop name" data-testid={`drop-edit-name-${d.id}`} />
          <input value={form.tagline} onChange={(e) => setForm({ ...form, tagline: e.target.value })}
            className={input} placeholder="Tagline" data-testid={`drop-edit-tagline-${d.id}`} />
          <input value={form.eta} onChange={(e) => setForm({ ...form, eta: e.target.value })}
            className={input} placeholder="ETA (e.g. September 2026)" data-testid={`drop-edit-eta-${d.id}`} />
          <div className="flex gap-3">
            <button onClick={save} disabled={busy} data-testid={`drop-save-${d.id}`}
              className="font-mono text-[11px] uppercase tracking-wider border border-signal text-signal px-4 py-2 hover:bg-signal hover:text-ink transition-colors disabled:opacity-50">Save</button>
            <button onClick={() => setEditing(false)} className="font-mono text-[11px] uppercase tracking-wider text-graphite px-2">Cancel</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="border border-border p-5" data-testid={`drop-row-${d.id}`}>
      <div className="flex flex-wrap items-center gap-4 justify-between">
        <div>
          <div className="flex items-center gap-3">
            <p className="font-medium text-sm">{d.name}</p>
            <span className={`font-mono text-[9px] uppercase tracking-[0.2em] px-2 py-0.5 border ${
              d.status === "live" ? "text-pulse border-pulse/50" : "text-signal border-signal/40"
            }`}>{d.status}</span>
          </div>
          <p className="font-mono text-[10px] uppercase tracking-wider text-graphite mt-1.5">
            {d.eta || "—"} · {d.waitlist_count} on waitlist{d.status === "upcoming" ? ` (${d.pending_count} to notify)` : ""}
          </p>
        </div>
        <div className="flex gap-3">
          <button onClick={startEdit} data-testid={`drop-edit-${d.id}`}
            className="font-mono text-[11px] uppercase tracking-wider border border-border px-4 py-2 hover:border-chalk transition-colors">Edit</button>
          {d.status === "upcoming" && (
            <button onClick={() => onGolive(d.id)} disabled={busy} data-testid={`drop-golive-${d.id}`}
              className="font-mono text-[11px] uppercase tracking-wider bg-pulse text-ink px-4 py-2 hover:bg-pulse/85 transition-colors disabled:opacity-50">Go Live</button>
          )}
        </div>
      </div>
    </div>
  );
}

export default function AdminDrops() {
  const [drops, setDrops] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/admin/drops").then((r) => setDrops(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return;
    setBusy(true);
    try {
      await api.post("/admin/drops", form);
      setForm(EMPTY);
      toast.success("Drop created.");
      load();
    } catch { toast.error("Could not create drop."); }
    setBusy(false);
  };

  const save = async (id, editForm) => {
    setBusy(true);
    let ok = false;
    try {
      await api.put(`/admin/drops/${id}`, editForm);
      toast.success("Drop updated.");
      ok = true;
      load();
    } catch { toast.error("Could not update drop."); }
    setBusy(false);
    return ok;
  };

  const golive = async (id) => {
    if (!window.confirm("Launch this drop and email everyone on the waitlist?")) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/drops/${id}/golive`);
      if (data.attempted > 0 && data.notified < data.attempted) {
        toast.warning(`Drop is live — ${data.notified}/${data.attempted} waitlist emails delivered. Undelivered stay pending for retry.`);
      } else {
        toast.success(`Drop is live — ${data.notified} waitlist email${data.notified === 1 ? "" : "s"} sent.`);
      }
      load();
    } catch { toast.error("Could not launch drop."); }
    setBusy(false);
  };

  return (
    <div className="mt-20" data-testid="admin-drops">
      <div className="flex items-baseline justify-between mb-2">
        <h2 className="font-anton uppercase text-3xl">Drops</h2>
        <span className="font-mono text-[11px] uppercase tracking-wider text-graphite">Admin only</span>
      </div>
      <p className="text-graphite text-sm mb-8">Create and edit limited drops. "Go Live" emails everyone on the waitlist automatically.</p>

      <div className="space-y-3">
        {drops.map((d) => (
          <DropRow key={d.id} drop={d} busy={busy} onSave={save} onGolive={golive} />
        ))}
      </div>

      <form onSubmit={create} className="border border-dashed border-border p-5 mt-4 space-y-3" data-testid="drop-create-form">
        <p className="font-mono text-[11px] uppercase tracking-wider text-graphite">New drop</p>
        <div className="grid sm:grid-cols-3 gap-4">
          <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
            className={input} placeholder="Drop name" required data-testid="drop-new-name" />
          <input value={form.tagline} onChange={(e) => setForm({ ...form, tagline: e.target.value })}
            className={input} placeholder="Tagline" data-testid="drop-new-tagline" />
          <input value={form.eta} onChange={(e) => setForm({ ...form, eta: e.target.value })}
            className={input} placeholder="ETA (e.g. September 2026)" data-testid="drop-new-eta" />
        </div>
        <button type="submit" disabled={busy} data-testid="drop-create-btn"
          className="font-mono text-[11px] uppercase tracking-wider border border-chalk px-5 py-2 hover:bg-chalk hover:text-ink transition-colors disabled:opacity-50">
          Create Drop
        </button>
      </form>
    </div>
  );
}
